import type { SourceLanguageCode, TargetLanguageCode } from '../constants/languages.ts';
import type { ProgressionZone } from './level-progression.ts';

// =============================================================================
// Chapter metadata — the 4 sections of a complete workbook
// =============================================================================

export interface ChapterMeta {
  /** Chapter number (1-4). */
  number: number;
  /** Chapter title in the target language. */
  title: string;
  /** Chapter title in the learner's native language. */
  titleNative: string;
  /** Which progression zone this chapter covers. */
  zone: ProgressionZone;
  /** First block index (1-based, inclusive). */
  startBlock: number;
  /** Last block index (1-based, inclusive). */
  endBlock: number;
}

// =============================================================================
// Chapter title translations
// =============================================================================

/**
 * Chapter titles in each target language. The keys are internal identifiers
 * matching the 4 progression zones.
 */
const CHAPTER_TITLES: Record<TargetLanguageCode, Record<ProgressionZone, string>> = {
  es: {
    foundation: 'Primeros Pasos',
    consolidation: 'Ganando Confianza',
    transition: 'Creciendo Juntos',
    arrival: 'Alzando el Vuelo',
  },
  fr: {
    foundation: 'Premiers Pas',
    consolidation: 'Prendre Confiance',
    transition: 'En Progression',
    arrival: 'Prendre Son Envol',
  },
  pt: {
    foundation: 'Primeiros Passos',
    consolidation: 'Ganhando Confianca',
    transition: 'Crescendo Juntos',
    arrival: 'Alcando Voo',
  },
  it: {
    foundation: 'Primi Passi',
    consolidation: 'Prendere Fiducia',
    transition: 'Crescere Insieme',
    arrival: 'Spiccare il Volo',
  },
  en: {
    foundation: 'First Steps',
    consolidation: 'Building Confidence',
    transition: 'Growing Stronger',
    arrival: 'Taking Flight',
  },
  de: {
    foundation: 'Erste Schritte',
    consolidation: 'Vertrauen Aufbauen',
    transition: 'Stärker Werden',
    arrival: 'Flügel Ausbreiten',
  },
};

/**
 * Native language chapter titles (English and Dutch).
 */
const CHAPTER_TITLES_NATIVE: Record<SourceLanguageCode, Record<ProgressionZone, string>> = {
  en: {
    foundation: 'First Steps',
    consolidation: 'Building Confidence',
    transition: 'Growing Stronger',
    arrival: 'Taking Flight',
  },
  nl: {
    foundation: 'Eerste Stappen',
    consolidation: 'Vertrouwen Opbouwen',
    transition: 'Sterker Worden',
    arrival: 'Vleugels Uitslaan',
  },
  es: {
    foundation: 'Primeros Pasos',
    consolidation: 'Ganando Confianza',
    transition: 'Creciendo Juntos',
    arrival: 'Alzando el Vuelo',
  },
};

// =============================================================================
// Chapter block ranges — how the 60 blocks are divided
// =============================================================================

/**
 * Default block ranges for a 60-block complete workbook.
 * These map directly to the progression zones.
 */
const CHAPTER_BLOCK_RANGES: Array<{
  zone: ProgressionZone;
  startBlock: number;
  endBlock: number;
}> = [
  { zone: 'foundation', startBlock: 1, endBlock: 15 },
  { zone: 'consolidation', startBlock: 16, endBlock: 30 },
  { zone: 'transition', startBlock: 31, endBlock: 48 },
  { zone: 'arrival', startBlock: 49, endBlock: 60 },
];

// =============================================================================
// Public API
// =============================================================================

/**
 * Builds chapter metadata for a complete workbook.
 *
 * @param targetLanguage - The language the learner is studying
 * @param nativeLanguage - The learner's native language ('en' or 'nl')
 * @param totalBlocks - Total blocks in the workbook (used to scale ranges if not 60)
 * @returns Array of 4 ChapterMeta objects
 */
export function buildChapters(
  targetLanguage: TargetLanguageCode,
  nativeLanguage: SourceLanguageCode,
  totalBlocks = 60,
): ChapterMeta[] {
  const targetTitles = CHAPTER_TITLES[targetLanguage];
  const nativeTitles = CHAPTER_TITLES_NATIVE[nativeLanguage];

  // If total blocks differs from 60, scale the ranges proportionally
  if (totalBlocks !== 60) {
    const scale = totalBlocks / 60;
    return CHAPTER_BLOCK_RANGES.map((range, idx) => ({
      number: idx + 1,
      title: targetTitles[range.zone],
      titleNative: nativeTitles[range.zone],
      zone: range.zone,
      startBlock: Math.round((range.startBlock - 1) * scale) + 1,
      endBlock: Math.min(Math.round(range.endBlock * scale), totalBlocks),
    }));
  }

  return CHAPTER_BLOCK_RANGES.map((range, idx) => ({
    number: idx + 1,
    title: targetTitles[range.zone],
    titleNative: nativeTitles[range.zone],
    zone: range.zone,
    startBlock: range.startBlock,
    endBlock: range.endBlock,
  }));
}

/**
 * Returns which chapter a given block belongs to.
 *
 * @param blockOrder - The block's 1-based position
 * @param totalBlocks - Total blocks in the workbook
 * @returns The chapter number (1-4) or undefined if out of range
 */
export function getChapterForBlock(blockOrder: number, totalBlocks = 60): number | undefined {
  const chapters = buildChapters('es', 'en', totalBlocks); // Language doesn't matter for ranges
  const chapter = chapters.find((ch) => blockOrder >= ch.startBlock && blockOrder <= ch.endBlock);
  return chapter?.number;
}
